// Page previews with pdf.js (render only — all PDF output is written by pdf-lib).
// pdf.js is loaded on first use, so pages without PDFs stay light (NFR-5).
import type { PDFDocumentProxy } from "pdfjs-dist";
import { renderScale } from "@/lib/pdf/to-image";

type PdfJs = typeof import("pdfjs-dist");
let lib: Promise<PdfJs> | null = null;

// The "legacy" build includes fallbacks for slightly older browsers (e.g. iOS
// Safari a version or two back); the default build needs the very newest.
function pdfjs(): Promise<PdfJs> {
  lib ??= import("pdfjs-dist/legacy/build/pdf.mjs").then((m) => {
    m.GlobalWorkerOptions.workerPort = new Worker(
      new URL("./pdfjs.worker.ts", import.meta.url),
      { type: "module" },
    );
    return m;
  });
  return lib;
}

export async function openPdf(file: Blob): Promise<PDFDocumentProxy> {
  const { getDocument } = await pdfjs();
  try {
    return await getDocument({ data: new Uint8Array(await file.arrayBuffer()) })
      .promise;
  } catch (error) {
    if ((error as Error)?.name === "PasswordException") {
      throw new Error(
        "This PDF is password-protected. Please remove the password first.",
      );
    }
    throw new Error("This PDF couldn't be opened. It may be damaged.");
  }
}

/** Draws one page (1-based) to an image `width` pixels wide, or at `scale`. */
export async function renderPage(
  doc: PDFDocumentProxy,
  pageNumber: number,
  size: { width: number } | { scale: number },
  type: "image/png" | "image/jpeg" = "image/png",
  quality = 0.92,
): Promise<Blob> {
  const page = await doc.getPage(pageNumber);
  const base = page.getViewport({ scale: 1 });
  const scale = "width" in size ? size.width / base.width : size.scale;
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(viewport.width));
  canvas.height = Math.max(1, Math.round(viewport.height));
  await page.render({ canvas, viewport, background: "#ffffff" }).promise;
  page.cleanup();
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("The page couldn't be drawn.")),
      type,
      quality,
    ),
  );
}

export type PdfSummary =
  | {
      pages: number;
      thumb: Blob;
      /** First page size in points (1/72 inch), as displayed. */
      size: { width: number; height: number };
    }
  | { error: string };

const summaries = new WeakMap<Blob, Promise<PdfSummary>>();

/** Page count and a first-page thumbnail, cached per file. */
export function summarizePdf(file: Blob, width = 160): Promise<PdfSummary> {
  let summary = summaries.get(file);
  if (!summary) {
    summary = (async () => {
      let doc: PDFDocumentProxy | null = null;
      try {
        doc = await openPdf(file);
        const first = (await doc.getPage(1)).getViewport({ scale: 1 });
        const thumb = await renderPage(doc, 1, { width });
        return {
          pages: doc.numPages,
          thumb,
          size: { width: first.width, height: first.height },
        };
      } catch (error) {
        return { error: (error as Error).message };
      } finally {
        await doc?.loadingTask.destroy();
      }
    })();
    summaries.set(file, summary);
  }
  return summary;
}

/** Renders every page's thumbnail in turn, reporting each as it's ready.
 *  Returns a function that stops early (e.g. when the file is removed). */
export function renderAllPages(
  file: Blob,
  width: number,
  onCount: (pages: number) => void,
  onPage: (index: number, thumb: Blob) => void,
  onError: (message: string) => void,
): () => void {
  let stopped = false;
  (async () => {
    let doc: PDFDocumentProxy | null = null;
    try {
      doc = await openPdf(file);
      if (stopped) return;
      onCount(doc.numPages);
      for (let i = 1; i <= doc.numPages && !stopped; i++) {
        const thumb = await renderPage(doc, i, { width });
        if (!stopped) onPage(i - 1, thumb);
      }
    } catch (error) {
      if (!stopped) onError((error as Error).message);
    } finally {
      await doc?.loadingTask.destroy();
    }
  })();
  return () => {
    stopped = true;
  };
}

/** PDF to image (FR-15): renders the chosen pages (0-based) one at a time.
 *  pdf.js parses in its own worker and draws in short slices, so the page stays
 *  responsive; `onProgress` reports each finished page. */
export async function renderPagesToImages(
  file: Blob,
  pages: number[],
  dpi: number,
  type: "image/jpeg" | "image/png",
  onProgress: (done: number) => void,
): Promise<
  {
    page: number;
    blob: Blob;
    width: number;
    height: number;
    reduced: boolean;
  }[]
> {
  const doc = await openPdf(file);
  try {
    const out = [];
    for (const [i, index] of pages.entries()) {
      const page = await doc.getPage(index + 1);
      const size = renderScale(page.getViewport({ scale: 1 }), dpi);
      const blob = await renderPage(
        doc,
        index + 1,
        { scale: size.scale },
        type,
      );
      out.push({ page: index, blob, ...size });
      onProgress(i + 1);
    }
    return out;
  } finally {
    await doc.loadingTask.destroy();
  }
}

const openDocs = new WeakMap<Blob, Promise<PDFDocumentProxy>>();

/** One open document per file, kept while an editor uses it. */
export function cachedPdf(file: Blob): Promise<PDFDocumentProxy> {
  let doc = openDocs.get(file);
  if (!doc) {
    doc = openPdf(file);
    openDocs.set(file, doc);
    doc.catch(() => openDocs.delete(file));
  }
  return doc;
}

/** A page's size as shown (points), for the editor. */
export async function shownPageSize(file: Blob, pageIndex: number) {
  const doc = await cachedPdf(file);
  const viewport = (await doc.getPage(pageIndex + 1)).getViewport({ scale: 1 });
  return { width: viewport.width, height: viewport.height };
}

/** The page drawn `width` pixels wide, for the editor background. */
export async function renderForEditor(
  file: Blob,
  pageIndex: number,
  width: number,
) {
  const doc = await cachedPdf(file);
  return renderPage(doc, pageIndex + 1, { width }, "image/png");
}
